import {
	LayoutDashboard,
	Zap,
	Users,
	Sparkles,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import type { Page } from "@/lib/types";

interface LayoutProps {
	currentPage: Page;
	onNavigate: (page: Page) => void;
	children: React.ReactNode;
}

const navItems: { page: Page; label: string; icon: React.ReactNode }[] = [
	{ page: "dashboard", label: "Dashboard", icon: <LayoutDashboard className="size-5" /> },
	{ page: "sequences", label: "Sequences", icon: <Zap className="size-5" /> },
	{ page: "contacts", label: "Contacts", icon: <Users className="size-5" /> },
	{ page: "ai-writer", label: "AI Writer", icon: <Sparkles className="size-5" /> },
];

export function Layout({ currentPage, onNavigate, children }: LayoutProps) {
	const [collapsed, setCollapsed] = useState(false);

	return (
		<div className="flex h-screen overflow-hidden bg-background">
			{/* Sidebar */}
			<aside
				className={`flex flex-col border-r border-border bg-sidebar transition-all duration-300 ${collapsed ? "w-16" : "w-60"}`}
			>
				{/* Nav Items */}
				<nav className="flex-1 p-2 space-y-1">
					{navItems.map((item) => {
						const isActive =
							currentPage === item.page ||
							(item.page === "sequences" && currentPage === "sequence-detail");
						return (
							<button
								key={item.page}
								onClick={() => onNavigate(item.page)}
								className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all
									${isActive
										? "bg-neon-cyan/10 text-neon-cyan"
										: "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
									}
									${collapsed ? "justify-center" : ""}
								`}
							>
								{item.icon}
								{!collapsed && <span>{item.label}</span>}
							</button>
						);
					})}
				</nav>

				<Separator />

				{/* Collapse toggle */}
				<div className="p-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setCollapsed(!collapsed)}
						className="w-full justify-center text-muted-foreground hover:text-foreground"
					>
						{collapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
					</Button>
				</div>
			</aside>

			{/* Main Content */}
			<main className="flex-1 overflow-auto">{children}</main>
		</div>
	);
}
